import React, { useState, useEffect, useRef } from 'react';
import { Users, MessageCircle, Send, X, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import CollaborationTimePanel from './CollaborationTimePanel';
import CaseMembers from './teams/CaseMembers';
import caseMembersService from '../services/caseMembersService';
import { teamsApi, communicationsApi } from '../services';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CollaborationPanel = ({ caseId, caseData }: { caseId: string; caseData?: any }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  const isEnglish = currentLanguage === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [selectedCollab, setSelectedCollab] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [userRole, setUserRole] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (caseId) {
      loadCollaborators();
      fetchUserRole();
    }
  }, [caseId]);

  const fetchUserRole = async () => {
    try {
      const members = await caseMembersService.getCaseMembers(caseId);
      const myMembership = members.find((m: any) => m.user_id === currentUser.id);
      const role = myMembership?.role?.toUpperCase() || null;
      const token = localStorage.getItem('token');
      const caseResponse = await axios.get(`${BACKEND_URL}/api/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const isOwner = caseResponse.data?.assigned_to === currentUser.id || caseResponse.data?.lawyer_id === currentUser.id;
      setUserRole(role || (isOwner ? 'LEAD' : null));
    } catch (error) {
      console.error('Failed to fetch user role:', error);
    }
  };

  useEffect(() => {
    if (selectedCollab) {
      loadMessages();
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedCollab]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadCollaborators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/collaborations/case/${caseId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCollaborators(response.data.collaborators || []);
    } catch (error) {
      console.error('Failed to load collaborators:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedCollab) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/collaborations/${selectedCollab.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/collaborations/${selectedCollab.id}/message`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewMessage('');
      loadMessages();
    } catch (error) {
      toast.error(t('Could not send message', 'Kunne ikke sende melding'));
    } finally {
      setSending(false);
    }
  };

  const getAccessLabel = (level: string) => {
    const labels: Record<string, string> = { READ_ONLY: t('Read only', 'Kun lese'), COMMENT: t('Comment', 'Kommentere'), EDIT: t('Edit', 'Redigere') };
    return labels[level] || level;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = { PENDING: 'bg-amber-50 text-amber-600', ACTIVE: 'bg-emerald-50 text-emerald-600', COMPLETED: 'bg-blue-50 text-blue-600', DECLINED: 'bg-red-50 text-red-600' };
    return colors[status] || colors.PENDING;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { PENDING: t('Pending', 'Venter'), ACTIVE: t('Active', 'Aktiv'), COMPLETED: t('Completed', 'Fullført'), DECLINED: t('Declined', 'Avslått') };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-4xl mx-auto px-8 py-10">
        <header className="mb-10">
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">
            {t('Resources', 'Ressurser')}
          </h1>
          <p className="text-gray-500 text-sm">
            {t('Manage team members and collaborators', 'Administrer teammedlemmer og samarbeidspartnere')}
          </p>
        </header>

        <section className="mb-12">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-5">
            {t('Team Members', 'Teammedlemmer')}
          </h2>
          <CaseMembers 
            caseId={caseId}
            currentUserId={currentUser.id}
            isLead={userRole === 'LEAD' || userRole === 'lead' || userRole === 'owner'}
          />
        </section>

        {collaborators.length > 0 && (
          <section className="pt-10 border-t border-gray-100">
            <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-5">
              {t('External Partners', 'Eksterne partnere')}
            </h2>
            <div className="space-y-3">
              {collaborators.map((collab: any) => (
                <button
                  key={collab.id}
                  onClick={() => setSelectedCollab(collab)}
                  className="w-full p-5 text-left rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                        <span className="text-sm font-medium text-gray-600">
                          {collab.expert_name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{collab.expert_name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{getAccessLabel(collab.access_level)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {collab.total_hours > 0 && <span className="text-xs text-gray-500">{collab.total_hours}t</span>}
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(collab.status)}`}>
                        {getStatusLabel(collab.status)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      {selectedCollab && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/10 backdrop-blur-sm" onClick={() => setSelectedCollab(null)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setSelectedCollab(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(selectedCollab.status)}`}>
                  {getStatusLabel(selectedCollab.status)}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">{selectedCollab.expert_name}</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedCollab.case_title}</p>
            </div>

            <div className="flex border-b border-gray-100">
              {[{ id: 'chat', icon: MessageCircle, label: 'Chat' }, { id: 'time', icon: Clock, label: t('Hours', 'Timer') }].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
              {activeTab === 'chat' ? (
                <div className="p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center py-16">
                      <MessageCircle className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm">{t('No messages yet', 'Ingen meldinger ennå')}</p>
                    </div>
                  ) : (
                    messages.map((msg: any) => (
                      <div key={msg.id} className={`flex ${msg.sender_id === currentUser.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${msg.sender_id === currentUser.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
                          <div className="text-sm">{msg.message}</div>
                          <div className={`text-[10px] mt-1 ${msg.sender_id === currentUser.id ? 'text-gray-400' : 'text-gray-400'}`}>
                            {new Date(msg.timestamp).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="p-6">
                  <CollaborationTimePanel collaborationId={selectedCollab.id} isExpert={currentUser.id === selectedCollab.expert_id} />
                </div>
              )}
            </div>

            {activeTab === 'chat' && selectedCollab.status === 'ACTIVE' && (
              <form onSubmit={sendMessage} className="p-4 border-t border-gray-100">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('Write a message...', 'Skriv en melding...')}
                    className="flex-1 px-4 py-2.5 bg-gray-50 border-0 rounded-full text-sm focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationPanel;
